import type { DiscoverSwissAccessibilityFragment } from '#build/graphql-operations'
import { notNullish } from '@vueuse/core'
import { mapAccessibilityToAmenityFeatures } from '~/helpers/schemaOrg/accessibility'

type AccessibilityPoi = {
  accessibility?: Array<DiscoverSwissAccessibilityFragment | null> | null
  link?: Array<{ url?: string | null; type?: string | null } | null> | null
}

export async function useAccessibilityInfo(
  nid: MaybeRefOrGetter<string | undefined>,
) {
  const langcode = useCurrentLangcodeEnum()
  const resolvedNid = computed(() => toValue(nid) || '')

  const variables = computed(() => ({
    id: resolvedNid.value,
    langcode: langcode.value,
  }))

  // useAsyncGraphqlQuery dedupes by operation name + variables hash, so
  // multiple callers with the same nid/langcode (e.g. the address card UI and
  // the node JSON-LD) share a single request.
  const { data } = await useAsyncGraphqlQuery(
    'loadAccessibilityInfo',
    variables,
    {
      transform(data) {
        const entity = data.data.entityById
        if (
          entity &&
          'discoverSwissItem' in entity &&
          entity.discoverSwissItem?.poi
        ) {
          return entity.discoverSwissItem.poi as AccessibilityPoi
        }

        return null
      },
      deep: false,
      graphqlCaching: {
        client: true,
      },
      watch: [variables],
    },
  )

  const grades = computed<DiscoverSwissAccessibilityFragment[]>(() =>
    (data.value?.accessibility || []).filter(notNullish),
  )

  const url = computed<string | undefined>(() => {
    const link = data.value?.link?.find((v) => v?.type === 'GintoWeb')
    return link?.url ?? undefined
  })

  const amenityFeatures = computed(() =>
    mapAccessibilityToAmenityFeatures(grades.value),
  )

  return {
    grades,
    url,
    amenityFeatures,
  }
}
